import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { Bell, CalendarDays, ListChecks, Lock, MessageCircle, Phone, RefreshCw, Search, Plus } from "lucide-react";
import { useTheme } from "../../components/Layout";
import "./chat.css";
import type { AppNotification, CallInfo, Message, Presence, Room, User } from "./types";
import { Avatar, ChatCtx, Ctx, Modal, Spinner, StatusKind } from "./ui";
import { call } from "./api";
import { connectSocket, disconnectSocket, getSocket } from "./socket";
import { beep, registerSW } from "./push";
import { fa } from "./jalali";
import RoomList from "./RoomList";
import ChatWindow, { Jump } from "./ChatWindow";
import TasksPanel, { TaskFormModal } from "./TasksPanel";
import CalendarPanel from "./CalendarPanel";
import { CallManager, CallRequest, CallsPanel } from "./CallOverlay";
import SearchPanel from "./SearchPanel";
import NotificationCenter from "./NotificationCenter";
import { NewChatDialog, ProfileDialog } from "./Dialogs";

type Tab = "chat" | "tasks" | "calendar" | "calls";
interface ToastItem { id: number; text: string; sub?: string; ok: boolean; onClick?: () => void }
interface Confirm { opts: { title: string; text?: string; confirm?: string; danger?: boolean }; resolve: (v: boolean) => void }
type Me = User & { manager_name?: string | null };

const previewOf = (m: Message) => m.type === "voice" ? "🎤 پیام صوتی" : m.type === "image" ? "🖼 عکس" : m.type === "file" ? "📎 " + (m.file_name || "فایل") : m.type === "call" ? "📞 تماس" : (m.content || "").slice(0, 90);
const sortRooms = (l: Room[]) => [...l].sort((a, b) => (b.pinned - a.pinned) || ((b.last_message?.created_at || b.created_at) > (a.last_message?.created_at || a.created_at) ? 1 : -1));

export default function ChatPage() {
  const { dark } = useTheme();
  const [boot, setBoot] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState("");
  const [me, setMe] = useState<Me | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tab, setTab] = useState<Tab>("chat");
  const [activeId, setActiveId] = useState<number | null>(null);
  const [jump, setJump] = useState<Jump>({ nonce: 0 });
  const [presence, setPresence] = useState<Record<number, Presence>>({});
  const [socketUp, setSocketUp] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [notifs, setNotifs] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMore, setNotifMore] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [search, setSearch] = useState<{ roomId?: number } | null>(null);
  const [taskForm, setTaskForm] = useState<null | { assignee_id?: number; room_id?: number; title?: string; description?: string }>(null);
  const [taskFocus, setTaskFocus] = useState<number | null>(null);
  const [taskBadge, setTaskBadge] = useState(0);
  const [callReq, setCallReq] = useState<CallRequest | null>(null);
  const [incoming, setIncoming] = useState<CallInfo | null>(null);

  const roomsRef = useRef<Room[]>([]); roomsRef.current = rooms;
  const activeRef = useRef<number | null>(null); activeRef.current = activeId;
  const tabRef = useRef<Tab>("chat"); tabRef.current = tab;
  const meRef = useRef<Me | null>(null); meRef.current = me;
  const callRef = useRef<CallRequest | null>(null); callRef.current = callReq;
  const toastId = useRef(0);
  const lastNotif = useRef(0);
  const seenNotif = useRef<Set<number>>(new Set());
  const idleRef = useRef(false);

  // ─── ابزارها ───
  const pushToast = useCallback((t: Omit<ToastItem, "id">, ms = 3600) => {
    const id = ++toastId.current; setToasts(p => [...p.slice(-3), { ...t, id }]); setTimeout(() => setToasts(p => p.filter(x => x.id !== id)), ms);
  }, []);
  const toast = useCallback((text: string, ok = true) => pushToast({ text, ok }), [pushToast]);
  const ask: Ctx["ask"] = useCallback(opts => new Promise<boolean>(resolve => setConfirm({ opts, resolve })), []);

  const reloadRooms = useCallback(async () => {
    const r = await call("rooms");
    if (r.success) { roomsRef.current = r.rooms; setRooms(r.rooms); if (activeRef.current && !r.rooms.some((x: Room) => x.id === activeRef.current)) setActiveId(null); }
  }, []);
  const reloadUsers = useCallback(async () => { const r = await call("users"); if (r.success) setUsers(r.users); }, []);
  const reloadTaskBadge = useCallback(async () => { const r = await call("tasks", { scope: "mine", status: "open" }); if (r.success) setTaskBadge((r.counts?.review || 0) + (r.counts?.overdue || 0)); }, []);

  const openRoom: Ctx["openRoom"] = useCallback(async (id, opts) => {
    setTab("chat");
    if (id === activeRef.current && !opts) return;
    let room = roomsRef.current.find(r => r.id === id);
    if (!room) { await reloadRooms(); room = roomsRef.current.find(r => r.id === id); if (!room) return; }
    setActiveId(id);
    setJump({ messageId: opts?.messageId, replyTo: opts?.replyTo, nonce: Date.now(), unread: room.unread_count });
    setRooms(p => p.map(r => (r.id === id ? { ...r, unread_count: 0, mention_count: 0 } : r)));
  }, [reloadRooms]);

  const openDirect: Ctx["openDirect"] = useCallback(async (userId, replyTo) => {
    const r = await call("room_create", { type: "direct", members: [userId] });
    if (!r.success) return toast(r.message || "ساخت گفتگو ناموفق بود", false);
    await reloadRooms(); openRoom(r.room_id, { replyTo });
  }, [openRoom, reloadRooms, toast]);

  const startCall: Ctx["startCall"] = useCallback((roomId, type) => {
    if (callRef.current) return toast("هم‌اکنون در یک تماس هستید", false);
    setCallReq({ mode: "start", roomId, type });
  }, [toast]);
  const joinCall: Ctx["joinCall"] = useCallback((callId, roomId, type) => {
    if (callRef.current) return toast("هم‌اکنون در یک تماس هستید", false);
    setCallReq({ mode: "join", callId, roomId, type });
  }, [toast]);

  const markNotifRead = useCallback((ids: number[]) => {
    if (!ids.length) return; call("notifications_read", { ids });
    setNotifs(p => p.map(n => (ids.includes(n.id) ? { ...n, is_read: 1 } : n))); setUnread(u => Math.max(0, u - ids.length));
  }, []);
  const openFromData = useCallback((d: any) => {
    if (!d) return;
    if (d.task_id) { setTab("tasks"); setTaskFocus(+d.task_id); }
    else if (d.event_id && !(d.room_id > 0)) setTab("calendar");
    else if (d.room_id > 0) openRoom(+d.room_id, d.message_id ? { messageId: +d.message_id } : undefined);
    else if (d.event_id) setTab("calendar");
  }, [openRoom]);
  const openNotification = (n: AppNotification) => { if (!n.is_read) markNotifRead([n.id]); setNotifOpen(false); openFromData(n.data); };
  const handleNewNotif = (n: AppNotification) => {
    setNotifs(p => (p.some(x => x.id === n.id) ? p : [n, ...p])); beep("notify");
    pushToast({ text: n.title, sub: n.body, ok: true, onClick: () => openNotification(n) }, 6000);
    if (n.data?.task_id) window.dispatchEvent(new CustomEvent("cx:task-updated"));
  };
  notifs.forEach(n => { if (n.id > lastNotif.current) lastNotif.current = n.id; });

  // ─── راه‌اندازی ───
  const init = useCallback(async () => {
    setBoot("loading");
    const w = await call("whoami");
    if (!w.success) { setErr(w.message || "برای استفاده از گفتگو وارد سیستم شوید"); setBoot("error"); return; }
    setMe(w.user);
    const [u, r, n] = await Promise.all([call("users"), call("rooms"), call("notifications")]);
    if (u.success) setUsers(u.users); if (r.success) { setRooms(r.rooms); roomsRef.current = r.rooms; }
    if (n.success) { setNotifs(n.notifications); setUnread(n.unread); setNotifMore(n.notifications.length >= 40); }
    call("ping"); reloadTaskBadge(); registerSW();
    setBoot("ready");
    const qp = new URLSearchParams(window.location.search); const clean = () => window.history.replaceState(null, "", window.location.pathname);
    if (qp.get("join")) { const j = await call("room_join", { code: qp.get("join") }); clean(); if (j.success) { await reloadRooms(); openRoom(j.room_id); toast("به گروه پیوستید") } else toast(j.message || "لینک دعوت نامعتبر است", false); }
    else if (qp.get("room") || qp.get("task") || qp.get("event")) { const d = { room_id: qp.get("room"), message_id: qp.get("msg"), task_id: qp.get("task"), event_id: qp.get("event") }; clean(); setTimeout(() => openFromData(d), 100); }
  }, []); // eslint-disable-line
  useEffect(() => { init(); const un = () => { setErr("نشست شما منقضی شده است. دوباره وارد شوید."); setBoot("error"); }; window.addEventListener("chat:unauthorized", un); return () => { window.removeEventListener("chat:unauthorized", un); disconnectSocket(); }; }, []); // eslint-disable-line

  // ─── سوکت ───
  useEffect(() => {
    if (boot !== "ready" || !me) return;
    const s = connectSocket();
    const onConnect = () => { setSocketUp(true); reloadRooms(); reloadUsers(); };
    const onMsg = ({ message }: { message: Message }) => {
      const mine = message.sender_id === me.id; const viewing = activeRef.current === message.room_id && tabRef.current === "chat" && !document.hidden;
      const known = roomsRef.current.some(r => r.id === message.room_id);
      if (!known) { reloadRooms(); return; }
      setRooms(prev => sortRooms(prev.map(r => r.id !== message.room_id ? r : {
        ...r, last_message: { id: message.id, type: message.type, preview: message.type === "system" ? message.content || "" : previewOf(message), sender_id: message.sender_id, sender_name: message.sender_name, is_deleted: 0, created_at: message.created_at },
        unread_count: mine || viewing || message.type === "system" || message.type === "call" ? r.unread_count : r.unread_count + 1,
        mention_count: !mine && !viewing && message.mentions?.some(x => x.id === me.id) ? r.mention_count + 1 : r.mention_count,
      })));
      const room = roomsRef.current.find(r => r.id === message.room_id);
      if (!mine && !viewing && message.type !== "system" && message.type !== "call" && !room?.muted) beep("msg");
    };
    const onRead = ({ room_id, user_id, upto_id }: any) => setRooms(p => p.map(r => r.id !== room_id ? r : { ...r, members: r.members.map(m => m.id === user_id ? { ...m, last_read_id: Math.max(m.last_read_id || 0, upto_id) } : m) }));
    const onNotif = ({ notification }: { notification: AppNotification }) => {
      setNotifs(p => [notification, ...p]); setUnread(u => u + 1); beep("notify");
      pushToast({ text: notification.title, sub: notification.body, ok: true, onClick: () => openNotification(notification) }, 6000);
    };
    const onIncoming = ({ call: c }: { call: CallInfo }) => { if (callRef.current) { call("call_decline", { call_id: c.id }); return; } setIncoming(c); reloadRooms(); };
    const onCallEnded = ({ call_id }: { call_id: number }) => { setIncoming(i => (i?.id === call_id ? null : i)); reloadRooms(); };
    const onTask = () => { window.dispatchEvent(new CustomEvent("cx:task-updated")); };
    s.on("connect", onConnect); s.on("disconnect", () => setSocketUp(false));
    s.on("presence:list", ({ users }: any) => setPresence(users));
    s.on("presence:update", ({ user_id, online, state }: any) => setPresence(p => { const n = { ...p }; if (online) n[user_id] = state || "active"; else delete n[user_id]; return n; }));
    s.on("message:new", onMsg); s.on("room:updated", reloadRooms); s.on("read:update", onRead); s.on("notification:new", onNotif);
    s.on("task:updated", onTask); s.on("call:incoming", onIncoming); s.on("call:ended", onCallEnded); s.on("call:updated", reloadRooms);
    if (s.connected) setSocketUp(true);
    const sw = (e: MessageEvent) => { if (e.data?.type === "cx:open") openFromData(e.data.data); };
    navigator.serviceWorker?.addEventListener("message", sw);
    return () => { s.off("connect", onConnect); s.off("presence:list"); s.off("presence:update"); s.off("message:new", onMsg); s.off("room:updated", reloadRooms); s.off("read:update", onRead); s.off("notification:new", onNotif); s.off("task:updated", onTask); s.off("call:incoming", onIncoming); s.off("call:ended", onCallEnded); s.off("call:updated", reloadRooms); s.off("disconnect"); navigator.serviceWorker?.removeEventListener("message", sw); };
  }, [boot, me?.id]); // eslint-disable-line

  useEffect(() => { const h = () => reloadTaskBadge(); window.addEventListener("cx:task-updated", h); return () => window.removeEventListener("cx:task-updated", h); }, [reloadTaskBadge]);
  useEffect(() => { const h = () => reloadUsers(); window.addEventListener("cx:users-changed", h); return () => window.removeEventListener("cx:users-changed", h); }, [reloadUsers]);

  // حالت جایگزین بدون سوکت + تازه‌سازی دوره‌ای
  useEffect(() => {
    if (boot !== "ready") return;
    const a = setInterval(() => { if (!socketUp) reloadRooms(); }, 5000);
    const b = setInterval(reloadUsers, socketUp ? 90000 : 20000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [boot, socketUp, reloadRooms, reloadUsers]);

  // ─── حضور (ping) و همگام‌سازی اعلان‌ها و تماس ورودی ───
  useEffect(() => {
    if (boot !== "ready") return;
    let t: any; const act = () => { idleRef.current = false; clearTimeout(t); t = setTimeout(() => { idleRef.current = true; }, 120000); };
    const vis = () => { idleRef.current = document.hidden; if (!document.hidden) act(); };
    const evs = ["mousemove", "keydown", "pointerdown", "touchstart", "scroll"]; evs.forEach(e => window.addEventListener(e, act, { passive: true })); document.addEventListener("visibilitychange", vis); act();
    const ping = () => call("ping", { idle: idleRef.current });
    ping(); const iv = setInterval(ping, 20000);
    return () => { clearTimeout(t); clearInterval(iv); evs.forEach(e => window.removeEventListener(e, act)); document.removeEventListener("visibilitychange", vis); };
  }, [boot]);

  useEffect(() => {
    if (boot !== "ready") return;
    let stop = false;
    const tick = async () => {
      const r = await call("sync", { after_notif_id: lastNotif.current });
      if (stop || !r.success) return;
      (r.notifications as AppNotification[]).forEach(n => { if (!seenNotif.current.has(n.id)) { seenNotif.current.add(n.id); lastNotif.current = Math.max(lastNotif.current, n.id); handleNewNotif(n); } });
      setUnread(r.unread);
      const inc = r.incoming as CallInfo[];
      if (!callRef.current) setIncoming(prev => (inc.length ? (prev && inc.some(c => c.id === prev.id) ? prev : inc[0]) : null));
    };
    const iv = setInterval(tick, 4000); tick();
    return () => { stop = true; clearInterval(iv); };
  }, [boot]); // eslint-disable-line

  // حضور: فعال / غیرفعال (فقط با سرور realtime)
  useEffect(() => {
    if (!socketUp) return; let idle = false; let t: any;
    const set = (s: "active" | "idle") => { if ((s === "idle") !== idle) { idle = s === "idle"; getSocket()?.emit("presence:state", { state: s }); } };
    const act = () => { set("active"); clearTimeout(t); t = setTimeout(() => set("idle"), 120000); };
    const vis = () => (document.hidden ? set("idle") : act());
    const evs = ["mousemove", "keydown", "pointerdown", "touchstart", "scroll"]; evs.forEach(e => window.addEventListener(e, act, { passive: true })); document.addEventListener("visibilitychange", vis); act();
    return () => { clearTimeout(t); evs.forEach(e => window.removeEventListener(e, act)); document.removeEventListener("visibilitychange", vis); };
  }, [socketUp]);

  // عنوان تب
  const totalUnread = rooms.reduce((a, r) => a + (r.muted ? 0 : r.unread_count), 0);
  useEffect(() => { const base = document.title.replace(/^\(\d+\)\s*/, ""); document.title = (totalUnread + unread > 0 ? `(${totalUnread + unread}) ` : "") + base; return () => { document.title = base; }; }, [totalUnread, unread]);

  const userById = useCallback((id: number) => users.find(u => u.id === id), [users]);
  const roomById = useCallback((id: number) => rooms.find(r => r.id === id), [rooms]);
  const statusOf = useCallback((id: number): StatusKind => {
    if (me && id === me.id) return "online";
    if (socketUp) return presence[id] === "active" ? "online" : presence[id] === "idle" ? "idle" : "offline";
    const u: any = userById(id); return u?.is_online ? (u.idle ? "idle" : "online") : "offline";
  }, [presence, socketUp, users, me]); // eslint-disable-line

  const ctx = useMemo<Ctx | null>(() => me && ({
    me, users, rooms, userById, statusOf, socketUp, toast, ask, openRoom, reloadRooms, roomById, startCall, joinCall, presence,
    openTask: (id: number) => { setTab("tasks"); setTaskFocus(id); }, newTask: (p) => setTaskForm(p || {}), openDirect,
  }), [me, users, rooms, userById, statusOf, socketUp, toast, ask, openRoom, reloadRooms, roomById, startCall, joinCall, presence, openDirect]);

  const activeRoom = rooms.find(r => r.id === activeId) || null;

  // ─── صفحات وضعیت ───
  if (boot === "loading") return <div className={`cx ${dark ? "dark" : ""} boot`}><div className="cx-boot"><Spinner size={34} /><b>در حال آماده‌سازی گفتگو…</b></div></div>;
  if (boot === "error" || !me || !ctx) return (
    <div className={`cx ${dark ? "dark" : ""} boot`}><div className="cx-boot"><Lock size={34} /><b>{err || "خطا در اتصال"}</b><button className="cx-btn primary" onClick={init}><RefreshCw size={15} /> تلاش مجدد</button></div></div>
  );

  const railItems: [Tab, string, ReactElement, number][] = [["chat", "گفتگو", <MessageCircle size={22} />, totalUnread], ["tasks", "وظایف", <ListChecks size={22} />, taskBadge], ["calendar", "تقویم", <CalendarDays size={22} />, 0], ["calls", "تماس‌ها", <Phone size={22} />, 0]];

  return (
    <ChatCtx.Provider value={ctx}>
      <div className={`cx ${dark ? "dark" : ""} ${activeId && tab === "chat" ? "has-room" : ""}`} dir="rtl">
        <nav className="cx-rail" aria-label="بخش‌ها">
          <div className="cx-rail-top">
            {railItems.map(([k, label, icon, n]) => (
              <button key={k} className={`cx-rail-btn ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} title={label}>
                {icon}<span>{label}</span>{n > 0 && <i className="cx-badge">{n > 99 ? "۹۹+" : fa(n)}</i>}
              </button>
            ))}
          </div>
          <div className="cx-rail-bot">
            <span className={`cx-conn ${socketUp ? "up" : "down"}`} title={socketUp ? "اتصال لحظه‌ای برقرار است" : "حالت جایگزین (بدون اتصال لحظه‌ای)"} />
            <button className="cx-rail-btn" onClick={() => setSearch({})} title="جستجو"><Search size={21} /><span>جستجو</span></button>
            <button className="cx-rail-btn" onClick={() => setNotifOpen(true)} title="اعلان‌ها"><Bell size={21} /><span>اعلان‌ها</span>{unread > 0 && <i className="cx-badge">{unread > 99 ? "۹۹+" : fa(unread)}</i>}</button>
            <button className="cx-rail-avatar" onClick={() => setShowProfile(true)} title="پروفایل من"><Avatar name={me.name} src={me.avatar} size={38} status="online" /></button>
          </div>
        </nav>

        <main className="cx-main">
          {tab === "chat" && <>
            <RoomList activeId={activeId} onOpen={id => openRoom(id)} onNew={() => setShowNew(true)} />
            {activeRoom ? <ChatWindow key={activeRoom.id} room={activeRoom} jump={jump} onBack={() => setActiveId(null)} onSearch={id => setSearch({ roomId: id })} />
              : <div className="cx-welcome"><div className="cx-welcome-card"><span><MessageCircle size={40} /></span><h2>گفتگوی سازمانی</h2><p>پیام، تماس تصویری، وظیفه و تقویم — همه در یک‌جا. هیچ پیامی حذف نمی‌شود و هر ویرایشی برای همه ثبت می‌شود.</p><button className="cx-btn primary" onClick={() => setShowNew(true)}><Plus size={16} /> شروع گفتگوی جدید</button></div></div>}
          </>}
          {tab === "tasks" && <TasksPanel focusId={taskFocus} onFocusDone={() => setTaskFocus(null)} />}
          {tab === "calendar" && <CalendarPanel />}
          {tab === "calls" && <CallsPanel />}
        </main>

        {showNew && <NewChatDialog onClose={() => setShowNew(false)} />}
        {showProfile && <ProfileDialog onClose={() => setShowProfile(false)} onSaved={() => { call("whoami").then(w => w.success && setMe(w.user)); reloadUsers(); }} />}
        {search && <SearchPanel roomId={search.roomId} onClose={() => setSearch(null)} />}
        {taskForm && <TaskFormModal prefill={taskForm} onClose={() => setTaskForm(null)} />}
        {notifOpen && <NotificationCenter items={notifs} unread={unread} hasMore={notifMore} onClose={() => setNotifOpen(false)} onOpen={openNotification}
          onReadAll={() => { call("notifications_read", { all: 1 }); setNotifs(p => p.map(n => ({ ...n, is_read: 1 }))); setUnread(0); }}
          onMore={async () => { const last = notifs[notifs.length - 1]?.id; const r = await call("notifications", { before_id: last }); if (r.success) { setNotifs(p => [...p, ...r.notifications]); setNotifMore(r.notifications.length >= 40); } }} />}

        <CallManager request={callReq} incoming={incoming} onClear={() => setCallReq(null)} onIncomingClear={() => setIncoming(null)}
          onAccept={c => { setIncoming(null); setCallReq({ mode: "join", callId: c.id, roomId: c.room_id, type: c.type }); }} />

        {confirm && (
          <Modal title={confirm.opts.title} width={420} onClose={() => { confirm.resolve(false); setConfirm(null); }}
            footer={<div className="cx-actions-row"><button className="cx-btn" onClick={() => { confirm.resolve(false); setConfirm(null); }}>انصراف</button>
              <button className={`cx-btn ${confirm.opts.danger ? "danger" : "primary"}`} autoFocus onClick={() => { confirm.resolve(true); setConfirm(null); }}>{confirm.opts.confirm || "تأیید"}</button></div>}>
            {confirm.opts.text && <p className="cx-muted" style={{ margin: 0 }}>{confirm.opts.text}</p>}
          </Modal>
        )}

        <div className="cx-toasts" aria-live="polite">
          {toasts.map(t => <div key={t.id} className={`cx-toast ${t.ok ? "" : "err"} ${t.onClick ? "click" : ""}`} onClick={t.onClick}><b>{t.text}</b>{t.sub && <small>{t.sub}</small>}</div>)}
        </div>
      </div>
    </ChatCtx.Provider>
  );
}
